import { feedback } from 'app_honda_cars/utils/feedback';
import analyticsHelper from 'app_honda_cars/utils/analyticsHelper';
import frontendUtils from 'honda_cars_ecux/utils/frontendUtils';

/**
 * Sub-controller for Test Drive Flyout — Shopper Details (step 3) and lead submit.
 *
 * Reads Preferred Models and the confirmed Honda Centre from sibling view markup (not sibling
 * module state), POSTs to `Page-SubmitConfigTestDriveLead`, and on success advances to Booking
 * Confirmation.
 */
export const testDriveDetails = {
  /**
   * Bootstrap the Shopper Details view. Idempotent; safe to call once at boot.
   */
  testDriveDetailsView() {
    this.helpers.bootstrap();
  },

  /**
   * Controller / state container. Skipped by `processInclude` (not a function).
   */
  helpers: {
    /**
     * AU mobile after separators are stripped — matches `Page-SubmitConfigTestDriveLead`.
     *
     * @private
     * @readonly
     */
    MOBILE_PATTERN: /^(?:\+61|61|0)4\d{8}$/,

    /**
     * @private
     *
     * @type {boolean}
     */
    isWired: false,

    /**
     * Guards against overlapping AJAX submits.
     *
     * @private
     *
     * @type {boolean}
     */
    isSubmitting: false,

    /**
     * @private
     *
     * @type {{
     *   view: HTMLElement | null,
     *   modal: HTMLElement | null,
     *   form: HTMLFormElement | null,
     *   submitButton: HTMLButtonElement | null,
     *   toastContainer: HTMLElement | null,
     *   centreCall: HTMLElement | null
     * }}
     */
    elements: {
      view: null,
      modal: null,
      form: null,
      submitButton: null,
      toastContainer: null,
      centreCall: null
    },

    /**
     * Lazy-wire on first show; refresh the centre call line on every enter.
     */
    bootstrap() {
      document.body.addEventListener('amb:modal-view-changed', event => {
        if (event.detail?.view !== 'test-drive-details') {
          return;
        }

        this.wire();
        this.syncCentreCallLine();
      });
    },

    /**
     * Resolve element refs and attach listeners once.
     *
     * @returns {boolean} `true` once wiring has succeeded.
     */
    wire() {
      if (this.isWired) {
        return true;
      }

      const view = document.querySelector('.configurator-modal .modal-view[view="test-drive-details"]');

      if (!view) {
        return false;
      }

      const form = view.querySelector('#test-drive-details-form');
      const submitButton = view.querySelector('.test-drive-details__submit');

      if (!form || !submitButton) {
        return false;
      }

      this.elements.view = view;
      this.elements.modal = view.closest('.configurator-modal');
      this.elements.form = form;
      this.elements.submitButton = submitButton;
      this.elements.toastContainer = view.querySelector('.toast-container');
      this.elements.centreCall = view.querySelector('.test-drive-details__centre-call');

      form.addEventListener('submit', event => this.onSubmit(event));
      form.addEventListener('focusin', event => this.onFieldFocus(event));

      this.isWired = true;

      return true;
    },

    /**
     * Clear a field's invalid state when the shopper returns to it.
     *
     * @param {FocusEvent} event
     *
     * @private
     */
    onFieldFocus(event) {
      const field = event.target;

      if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement)) {
        return;
      }

      if (!this.elements.form?.contains(field)) {
        return;
      }

      this.clearFieldError(field);
    },

    /**
     * Name the confirmed centre in the call-confirmation line, or fall back when unknown.
     *
     * @private
     */
    syncCentreCallLine() {
      const centreCall = this.elements.centreCall;

      if (!centreCall) {
        return;
      }

      const dealerName = this.getConfirmedCentreName();
      const template = centreCall.dataset.callTemplate || '';
      const fallback = centreCall.dataset.callFallback || '';

      centreCall.textContent = dealerName && template
        ? template.replace('{0}', dealerName)
        : fallback;
    },

    /**
     * @returns {string}
     *
     * @private
     */
    getConfirmedCentreName() {
      const selected = document.querySelector(
        '.configurator-modal .modal-view[view="test-drive-honda-centre"] ' +
        'input[name="test-drive-location-dealers"]:checked'
      );

      return (selected?.dataset.dealername || '').trim();
    },

    /**
     * Validate, POST the lead, and stay on this view when the request fails.
     *
     * @param {SubmitEvent} event
     *
     * @private
     */
    onSubmit(event) {
      event.preventDefault();

      if (this.isSubmitting) {
        return;
      }

      if (!this.validate()) {
        return;
      }

      if (!this.hasCompleteLeadContext()) {
        this.notify();

        return;
      }

      const url = this.elements.form.getAttribute('action');

      if (!url) {
        return;
      }

      const $view = $(this.elements.view).parent();

      this.isSubmitting = true;
      this.elements.submitButton?.setAttribute('disabled', '');

      $.ajax({
        url,
        type: 'post',
        dataType: 'json',
        contentType: 'application/json',
        data: JSON.stringify(this.buildPayload()),
        beforeSend: () => $view.spinner().start(),
        success: response => {
          if (response?.messageMobile) {
            this.showPhoneError(response.messageMobile);

            return;
          }

          if (!response?.error) {
            this.onSuccess();
          } else {
            this.notify(response?.message);
          }
        },
        error: jqXHR => {
          this.notify(jqXHR?.responseJSON?.message);
        },
        complete: () => {
          this.isSubmitting = false;
          this.elements.submitButton?.removeAttribute('disabled');
          $view.spinner().stop();
        }
      });
    },

    /**
     * Strip spaces and hyphens for validation and the wire — leave the visible input alone.
     *
     * @param {string} value
     *
     * @returns {string}
     *
     * @private
     */
    normaliseMobile(value) {
      return String(value || '').replace(/[\s-]/g, '');
    },

    /**
     * HTML constraint validation with brochure-style `data-*-error` messages on the field.
     * Mobile is re-checked after separator stripping so spaced numbers never hit the server.
     *
     * @returns {boolean}
     *
     * @private
     */
    validate() {
      const form = this.elements.form;

      if (!form) {
        return false;
      }

      this.clearInvalidFeedback();

      if (!form.checkValidity()) {
        form.querySelectorAll(':invalid').forEach(node => {
          if (!(node instanceof HTMLInputElement || node instanceof HTMLSelectElement)) {
            return;
          }

          this.showFieldError(node, this.getConstraintMessage(node));
        });

        form.querySelector(':invalid')?.focus();

        return false;
      }

      const phone = form.querySelector('#test-drive-details-phone');

      if (phone instanceof HTMLInputElement && !this.MOBILE_PATTERN.test(this.normaliseMobile(phone.value))) {
        this.showPhoneError(phone.dataset.patternMismatch);

        return false;
      }

      return true;
    },

    /**
     * Prefer models and a confirmed centre must both be present before a lead goes out.
     *
     * @returns {boolean}
     *
     * @private
     */
    hasCompleteLeadContext() {
      const models = this.getPreferredModels();
      const selection = this.getCentreSelection();

      return models.length > 0 && !!selection.expertId && !!selection.addressPostcode;
    },

    /**
     * @param {HTMLInputElement | HTMLSelectElement} field
     *
     * @returns {string}
     *
     * @private
     */
    getConstraintMessage(field) {
      const { dataset, validity } = field;

      if (validity.valueMissing && dataset.missingError) {
        return dataset.missingError;
      }

      if (validity.patternMismatch && dataset.patternMismatch) {
        return dataset.patternMismatch;
      }

      if (validity.typeMismatch && dataset.typeMismatch) {
        return dataset.typeMismatch;
      }

      return field.validationMessage || '';
    },

    /**
     * @private
     */
    clearInvalidFeedback() {
      this.elements.form?.querySelectorAll('.is-invalid').forEach(node => {
        if (node instanceof HTMLElement) {
          this.clearFieldError(node);
        }
      });
    },

    /**
     * @param {HTMLElement} field
     * @param {string} message
     *
     * @private
     */
    showFieldError(field, message) {
      field.classList.add('is-invalid');

      const errorEl = field.id ? document.getElementById(field.id + '-error') : null;

      if (errorEl) {
        errorEl.textContent = message || ' ';
        errorEl.style.display = 'flex';
      }
    },

    /**
     * @param {HTMLElement} field
     *
     * @private
     */
    clearFieldError(field) {
      field.classList.remove('is-invalid');

      const errorEl = field.id ? document.getElementById(field.id + '-error') : null;

      if (errorEl) {
        errorEl.textContent = '';
        errorEl.style.display = '';
      }
    },

    /**
     * @param {string} [message]
     *
     * @private
     */
    showPhoneError(message) {
      const phone = this.elements.form?.querySelector('#test-drive-details-phone');

      if (!(phone instanceof HTMLInputElement)) {
        return;
      }

      this.showFieldError(
        phone,
        message || phone.dataset.patternMismatch || 'Please add valid phone number'
      );
      phone.focus();
    },

    /**
     * Build the AJAX payload for `Page-SubmitConfigTestDriveLead`.
     *
     * @returns {Record<string, unknown>}
     *
     * @private
     */
    buildPayload() {
      const form = this.elements.form;
      const formData = new FormData(form);
      const selection = this.getCentreSelection();

      return {
        PreferredModels: this.getPreferredModels(),
        ContactDetails: {
          Title: formData.get('Title') || '',
          FirstName: formData.get('FirstName') || '',
          Surname: formData.get('LastName') || '',
          Email: formData.get('Email') || '',
          Mobile: this.normaliseMobile(formData.get('Phone') || ''),
          IsInterestedInFinancing: false,
          IsInterestedInInsurance: false,
          PrivacyConsent: !!formData.get('PrivacyConsent'),
          AddressPostcode: selection.addressPostcode,
          DealerId: selection.dealerId,
          expertId: selection.expertId
        },
        GACLIENTID: analyticsHelper.getGAClientId(),
        GATRACKID: analyticsHelper.getGATrackId(),
        GAUSERID: analyticsHelper.getGAUserId(),
        GUID: analyticsHelper.getGAGUID(),
        MetaEventId: this.getMetaEventId()
      };
    },

    /**
     * Prefer the shared sidebar UUID when GTM has seeded it; otherwise mint one for this submit.
     * Does not write `window.sidenavTestDriveUUID`.
     *
     * @returns {string}
     *
     * @private
     */
    getMetaEventId() {
      if (typeof window !== 'undefined' && window.sidenavTestDriveUUID) {
        return window.sidenavTestDriveUUID;
      }

      return frontendUtils.generateUUID();
    },

    /**
     * Checked Preferred Model product ids from step 1 markup.
     *
     * The card list carries nameplate (master) ids. For the pre-selected / currently configured
     * model we swap in the configured variant pid so the lead reflects the shopper's actual
     * grade/colour; the backend resolves the rest from that exact variant. Other added models
     * keep their master id (no per-model config), so the backend falls back to the default variant.
     *
     * @returns {Array<{ productId: string }>}
     *
     * @private
     */
    getPreferredModels() {
      const list = document.querySelector(
        '.configurator-modal .modal-view[view="test-drive"] .test-drive-model-list'
      );
      const currentModelId = list?.dataset.currentModel || '';
      const configuredVariantId = this.getConfiguredVariantId();

      const inputs = document.querySelectorAll(
        '.configurator-modal .modal-view[view="test-drive"] .test-drive-model-list .model--input:checked'
      );

      return Array.from(inputs).map(input => {
        const masterId = input.dataset.productId || input.value || '';
        const isCurrent = !!masterId && masterId === currentModelId;

        return {
          productId: isCurrent && configuredVariantId ? configuredVariantId : masterId
        };
      });
    },

    /**
     * Configured variant pid from the live configurator summary — the same source GTM, finance
     * and brochure flows read. Falls back to an empty string so the caller keeps the master id
     * (preserving today's behavior) when the summary is missing or unparseable.
     *
     * @returns {string}
     *
     * @private
     */
    getConfiguredVariantId() {
      try {
        const raw = document
          .querySelector('.js-save-summary')
          ?.getAttribute('data-car-summary-data');

        return raw ? (JSON.parse(raw)?.data?.product?.id || '') : '';
      } catch (e) {
        return '';
      }
    },

    /**
     * Confirmed centre ids from step 2 hidden selection inputs.
     *
     * @returns {{ addressPostcode: string, dealerId: string, expertId: string }}
     *
     * @private
     */
    getCentreSelection() {
      const selection = document.querySelector(
        '.configurator-modal .modal-view[view="test-drive-honda-centre"] .test-drive-honda-centre__selection'
      );

      return {
        addressPostcode: selection?.querySelector('input[name="AddressPostcode"]')?.value || '',
        dealerId: selection?.querySelector('input[name="DealerId"]')?.value || '',
        expertId: selection?.querySelector('input[name="ExpertId"]')?.value || ''
      };
    },

    /**
     * Advance to Booking Confirmation and clear history so Back cannot reopen a submitted form.
     *
     * @private
     */
    onSuccess() {
      this.reportComplete();
      this.elements.form?.reset();
      this.clearInvalidFeedback();

      const modal = this.elements.modal;

      if (typeof modal?.setView !== 'function') {
        return;
      }

      if (typeof modal.clearHistory === 'function') {
        modal.clearHistory();
      }

      modal.setView('test-drive-confirmation');
    },

    /**
     * Bridge to `int_gtm` — the schema for this event lives there, not here.
     *
     * @private
     */
    reportComplete() {
      $('body').trigger('gtm:event', {
        callback: 'configureTestDriveFormComplete',
        data: $(this.elements.form)
      });
    },

    /**
     * Show the AJAX error via the shared toast helper; leave form values intact.
     *
     * @param {string} [message]
     *
     * @private
     */
    notify(message) {
      const text = message || this.elements.toastContainer?.dataset.errorMessage;

      if (!text) {
        return;
      }

      feedback.toast({
        message: text,
        toastClass: 'toast-error',
        delay: 8000,
        container: this.elements.toastContainer
      });
    }
  }
};
